import {
  AD_TYPES,
  AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES,
  AMAZON_BUSINESS_SITE_RESTRICTION,
  AUDIENCE_SEGMENT_TYPES,
  SB_BRAND_AD_TYPE,
  SB_CAMPAIGN_GOAL_OPTIONS,
  SD_CAMPAIGN_TYPE_OPTIONS,
} from "./constants";
import { roasToStoredAcos, toFloat } from "./utils";
import type { AdPlan, ToAdPlanDataContext } from "./types";
import type { CreateAdvertisement } from "../../../../zodios/apiDefinitions/api/ads";

const toInt = (v: number | string | null | undefined): number => (typeof v === "number" ? v : Number.parseInt(v ?? ""));

const toFloatOrNull = (v: number | string | null | undefined): number | null => {
  const n = toFloat(v);
  return Number.isNaN(n) ? null : n;
};

// Preserves the JS coercion of the original `(x as number) / 100`:
// Number(null) === 0, Number(undefined) === NaN.
const divBy100 = (v: number | null | undefined): number => Number(v) / 100;

type AdPlanPayload = Omit<
  CreateAdvertisement,
  | "ad_control_fields"
  | "notification_settings"
  | "attributes"
  | "creative"
  | "creative_display_metadata"
  | "landing_page"
> & {
  ad_control_fields: Record<string, unknown>;
  notification_settings: Record<string, unknown>;
  attributes: Record<string, unknown>;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  creative?: any;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  creative_display_metadata?: any;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  landing_page?: any;
};

/** Transform an ad plan form state into the API request body. */
export function toAdPlanData(adPlan: AdPlan | undefined, ctx: ToAdPlanDataContext): CreateAdvertisement | undefined {
  // Ensure adPlan data present before trying to process in case of new composite component being created
  if (!adPlan) {
    return;
  }

  const { adId, marketPlace, acosOrRoas, organizationType } = ctx;

  // The API treats a payload without an id as a create, and only creates are held to the new
  // Sponsored Brands Collection rules.
  const isCreatingAd = !(adId ?? adPlan.id);

  const productIds = adPlan.product?.map(toInt);
  const creativeProductIds = adPlan.creativeProduct?.map(toInt);
  const landingPageProductIds = adPlan.landingPageProduct?.map(toInt);
  const excludedProductIds = adPlan.excludedProducts?.map(toInt);

  // `brand`, `page`, `store` start as `""` in form state and become option objects once selected.
  const brand = typeof adPlan.brand === "object" ? adPlan.brand : undefined;
  const pageOption = typeof adPlan.page === "object" ? adPlan.page : undefined;

  const adType = typeof adPlan.adType === "number" ? adPlan.adType : null;
  const isAdType = (types: readonly number[]): boolean => adType !== null && types.includes(adType);

  const formatMatchTypes = (): string[] => {
    const matchTypeArray: string[] = [];
    if (adPlan.broadMatchEnabled) {
      matchTypeArray.push("broad");
    }

    if (adPlan.phraseMatchEnabled) {
      matchTypeArray.push("phrase");
    }

    if (adPlan.exactMatchEnabled) {
      matchTypeArray.push("exact");
    }

    return matchTypeArray;
  };

  const formatTargetCategories = (
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    targetCategories: Array<Record<string, any>> | undefined,
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  ): Array<Record<string, any>> | undefined => {
    const formattedTargetCategoryData = targetCategories?.map((targetDetails) => {
      if (targetDetails.asin_price_greater_than && targetDetails.asin_price_less_than) {
        targetDetails = {
          asin_price_between: `${targetDetails.asin_price_greater_than}-${targetDetails.asin_price_less_than}`,
          ...targetDetails,
        };
        delete targetDetails.asin_price_greater_than;
        delete targetDetails.asin_price_less_than;
      }

      if (targetDetails.asin_review_rating_greater_than && targetDetails.asin_review_rating_less_than) {
        targetDetails = {
          asin_review_rating_between: `${targetDetails.asin_review_rating_greater_than}-${targetDetails.asin_review_rating_less_than}`,
          ...targetDetails,
        };
        delete targetDetails.asin_review_rating_greater_than;
        delete targetDetails.asin_review_rating_less_than;
      }

      if (typeof targetDetails.brand === "object") {
        targetDetails.brand_name = targetDetails.brand.label;
        targetDetails.brand = targetDetails.brand.value;
      }

      delete targetDetails.savedToServer;

      return targetDetails;
    });

    return formattedTargetCategoryData;
  };

  const formatNegativeMatchTypes = (): string[] => {
    const negativeMatchTypeArray: string[] = [];
    if (adPlan.negativePhraseMatchEnabled) {
      negativeMatchTypeArray.push("phrase");
    }

    if (adPlan.negativeExactMatchEnabled) {
      negativeMatchTypeArray.push("exact");
    }

    return negativeMatchTypeArray;
  };

  const formatSDTargetTypes = (): string[] => {
    const sdTargetTypesArray: string[] = [];

    if (adPlan.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value) {
      if (adPlan.viewsSimilar) {
        sdTargetTypesArray.push("views_similar");
      }
      if (adPlan.viewsExact) {
        sdTargetTypesArray.push("views_exact");
      }
      if (adPlan.purchasesSimilar) {
        sdTargetTypesArray.push("purchases_similar");
      }
      if (adPlan.purchasesExact) {
        sdTargetTypesArray.push("purchases_exact");
      }
      if (adPlan.filledTargetAudiences?.length) {
        sdTargetTypesArray.push("audience");
      }
    }

    if (adPlan.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.CONTEXTUAL.value) {
      if (adPlan.sdDynamicSimilar) {
        sdTargetTypesArray.push("contextual_dynamic");
      }
    }

    return sdTargetTypesArray;
  };

  const data: AdPlanPayload = {
    id: adId ?? adPlan.id,
    products: productIds,
    category: adPlan.categoryId != null ? toInt(adPlan.categoryId) : undefined,
    name: adPlan.name,
    market_place: marketPlace,
    ad_type:
      adPlan.channel === "walmart" ?
        adPlan.adType === AD_TYPES.SPONSORED_PRODUCT.value ?
          AD_TYPES.SPONSORED_PRODUCT_WALMART.value
        : (adPlan.adType ?? undefined)
      : (adPlan.adType ?? undefined),
    targeting_type:
      adPlan.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value ? (adPlan.sdCampaignType ?? undefined) : adPlan.targetType,
    ad_control_fields: {
      min_bid: toFloat(adPlan.minBid),
      max_bid: toFloat(adPlan.maxBid),
      disable_bidding: adPlan.disableBidding ?? false,
      asin_harvesting: adPlan.asinHarvestingEnabled,
      cr_selector: adPlan.crSelectorEnabled,
      auto_negation: adPlan.autoNegationEnabled,
      target_acos_negation_threshold: divBy100(adPlan.maxAcosNegationThreshold),
      cumm_threshold: divBy100(adPlan.cumulativeNegationThreshold),
      keyword_harvesting: adPlan.keywordHarvestingEnabled,
      with_auto: adPlan.autoCampaignEnabled,
      broad_match_regular: true,
      broad_match_modifier: true,
      historical_keywords: adPlan.canHaveChildren ? true : adPlan.trellisTrakEnabled,
      bid_algo: adPlan.algorithm,
      discovery_budget_alloc: divBy100(adPlan.manualCampaignBudget),
      auto_budget_alloc: divBy100(adPlan.autoCampaignBudget),
      bid_strategy: adPlan.bidStrategy,
      sales_volume_control_slider: adPlan.salesVolumeControl,
      match_types: formatMatchTypes(),
      negative_match_types: formatNegativeMatchTypes(),
      lookback: adPlan.sdCampaignType === SD_CAMPAIGN_TYPE_OPTIONS.AUDIENCE.value ? adPlan.lookbackPeriod : [],
      tactic_type: adPlan.sdCampaignType,
      cost_type: adPlan.sdBidType,
      targets: formatSDTargetTypes(),

      campaign_placements:
        adPlan.adType === AD_TYPES.SPONSORED_PRODUCT_AUTO_WALMART.value ?
          null
        : {
            "Search Carousel": adPlan.searchCarouselCampaignPlacement,
            "Item Carousel": adPlan.itemCarouselCampaignPlacement,
            "Item Buybox": adPlan.buyBoxCarouselCampaignPlacement,
          },
      placement_bid_multipliers: {
        "Buy-Box": toFloat(adPlan.buyBoxPlacementBidMultiplier),
        "Search Ingrid": toFloat(adPlan.searchInGridPlacementBidMultiplier),
        "Stock Up": toFloat(adPlan.stockUpPlacementBidMultiplier),
        "Home Page": toFloat(adPlan.homePagePlacementBidMultiplier),
      },
      device_bid_multipliers: {
        Desktop: toFloat(adPlan.desktopPlatformBidMultiplier),
        Mobile: toFloat(adPlan.mobilePlatformBidMultiplier),
        App: toFloat(adPlan.appPlatformBidMultiplier),
      },
      suggested_keywords: adPlan.walmartSuggestedKeywordsEnabled,
      harvesting_setting_override: adPlan.manualHarvestingEnabled,
      harvesting_clicks: toFloatOrNull(adPlan.harvestingClicks),
      harvesting_conversions: toFloatOrNull(adPlan.harvestingConversions),
      harvesting_days: toFloatOrNull(adPlan.harvestingDays),
      harvesting_view_impressions: toFloatOrNull(adPlan.harvestingViewImpressions),
      acos_lookback_window: toInt(adPlan.acosLookbackWindow),
      cr_ctr_lookback_window: toInt(adPlan.crLookbackWindow),
      placement_multiplier: adPlan.algoManagedPlacementMultipliers ?? false,
      disable_acos_range: adPlan.ignoreSmartSpendACOSRange ?? false,
      site_restrictions: adPlan.siteRestrictions,
    },
    notification_settings: {
      max_acos: Number.parseInt(String(adPlan.notifications?.maxAcos ?? "")),
      max_spend: Number.parseFloat(String(adPlan.notifications?.maxSpend ?? "")),
      product_alert: adPlan.notifications?.productAlertEnabled ?? false,
    },

    user_category_id_list: [],
    daily_budget: adPlan.dailyBudget == null || adPlan.dailyBudget === "" ? undefined : Number(adPlan.dailyBudget),
    channel: adPlan.channel,
    state: adPlan.advStatus,
    is_composite: adPlan.canHaveChildren,
    attributes: {
      product_types: adPlan.filledProductKeys,
      brands: adPlan.filledProductBrands,
      attributes: adPlan.filteredAttributes,
    },
    non_trellis_enrolled_ad_accept_bids: adPlan.nonTrellisEnrolledAdAcceptBids,
    non_trellis_enrolled_ad: adPlan.nonTrellisEnrolledAd,
    non_trellis_enrolled_ad_accept_bids_start_date: adPlan.nonTrellisEnrolledAdAcceptBidsStartDate,

    tags: adPlan.tags ?? [],
  };
  data.themed_kw_list = adPlan.filledTags;
  data.themed_asin_list = adPlan.filledAsins?.map((asin) => asin.toUpperCase());
  data.discover_keywords = adPlan.keywordDiscoveryEnabled;

  if (!adPlan.canHaveChildren) {
    data.negative_kw_list = adPlan.filledNegativeKeywords;
    data.negative_asin_list = adPlan.filledNegativeAsins?.map((asin) => asin.toUpperCase());

    if (
      isAdType([
        AD_TYPES.SPONSORED_PRODUCT.value,
        AD_TYPES.SPONSORED_DISPLAY_V2.value,
        AD_TYPES.SPONSORED_BRAND.value,
        AD_TYPES.SPONSORED_BRAND_V2.value,
        AD_TYPES.SPONSORED_BRAND_VIDEO.value,
      ]) &&
      adPlan.channel !== "walmart"
    ) {
      data.user_category_id_list = formatTargetCategories(adPlan.filledTargetCategories) ?? [];
    }

    data.parent = adPlan.parent;
    data.target_acos =
      acosOrRoas === "roas" ? roasToStoredAcos(adPlan.targetACOS, adPlan.targetAcosFromServer) : adPlan.targetACOS;
    data.max_acos = acosOrRoas === "roas" ? roasToStoredAcos(adPlan.maxACOS, adPlan.maxAcosFromServer) : adPlan.maxACOS;
  }
  data.ad_goal = adPlan.goal;
  if (adPlan.parent) {
    data.budget_allocation = typeof adPlan.allocation === "number" ? adPlan.allocation : toFloat(adPlan.allocation);
  }
  if (adPlan.canHaveChildren || adPlan.parent) {
    data.attributes["competitors"] = adPlan.filledCompetitorBrands;
  }

  if (
    isAdType([
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_WALMART.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value,
    ])
  ) {
    data.brand_entity =
      organizationType !== "vendor" ? brand?.value
      : brand?.value !== null ? brand?.value
      : null;
    if (adPlan.storeLandingPage) {
      data.products = creativeProductIds;
      data.landing_page = {
        url: pageOption?.value,
      };
    }
    if (adPlan.newLandingPage || adPlan.newProductCollection || adPlan.manualCollection) {
      data.products = landingPageProductIds;
      data.landing_page = {
        productIds: landingPageProductIds,
      };
    }

    if ((adPlan.newProductCollection || adPlan.manualCollection) && pageOption?.label) {
      data.products = creativeProductIds;
    }

    if (adPlan.channel === "walmart") {
      if (adPlan.adType === AD_TYPES.SPONSORED_BRAND_VIDEO_WALMART.value) {
        data.creative = {
          caption_asset_id: adPlan.walmartCaptionAssetId,
          asset_id: adPlan.walmartAssetId,
        };
      }
      if (adPlan.adType === AD_TYPES.SPONSORED_BRAND_WALMART.value) {
        data.creative = {
          headline: adPlan.walmartHeadline,
          brand_name: adPlan.walmartBrandName,
          asset_id: adPlan.walmartAssetId,
          click_url: adPlan.walmartClickURL,
        };
      }
    } else {
      data.creative = {
        brandName: adPlan.creativeBrandName ? adPlan.creativeBrandName : (brand?.label ?? "Now"),
        brandLogoAssetID: adPlan.logo ? adPlan.logo : (adPlan.logoDetails?.asset_id ?? []),
        headline: adPlan.headline,
        productIds: creativeProductIds,
        shouldOptimizeAsins: false,
      };
      data.store_name = adPlan.store?.label;
      data.page_name = pageOption?.label;
    }
  }

  // Sponsored Brand Video data formatting for backend
  if (adPlan.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) {
    // All new ads use SB as the type on the backend despite being video (very confusing? can we not just align them for clarity to both be SBV?)
    if (!data.id || adPlan.sbV2) {
      data.ad_type = AD_TYPES.SPONSORED_BRAND_V2.value;
    }

    data.brand_entity =
      organizationType !== "vendor" ? brand?.value
      : brand?.value !== null ? brand?.value
      : null;

    // Core creatives for both video ad types
    data.creative = {
      properties: [
        {
          name: "trellis_brand_ad",
          state: "ENABLED",
          adGroupId: "adGroup1",
          creative: {},
        },
      ],
    };
    data.creative_display_metadata = {};
    // Regular video ad
    if (!adPlan.storeLandingPage) {
      data.creative.properties[0].creative = {
        productIds: productIds,
        videoAssetIds: [adPlan.video],
      };
      data.creative.brand_ad_type = SB_BRAND_AD_TYPE.VIDEO;
      data.ad_control_fields.tactic_type = SB_BRAND_AD_TYPE.VIDEO;
    }

    // Video ad with landing page option
    if (adPlan.storeLandingPage) {
      data.products = creativeProductIds;

      data.creative.properties[0].landingPage = {
        url: pageOption?.value,
        pageType: "STORE",
        productIds: landingPageProductIds,
      };

      data.creative.brand_ad_type = SB_BRAND_AD_TYPE.BRAND_VIDEO;
      data.creative.properties[0].creative = {
        brandName: adPlan.creativeBrandName ? adPlan.creativeBrandName : brand?.label,
        videoAssetIds: [adPlan.video],
        brandLogoAssetID: adPlan.logoDetails?.asset_id,
        headline: adPlan.headline,
        productIds: creativeProductIds,
      };
      data.ad_control_fields.tactic_type = SB_BRAND_AD_TYPE.BRAND_VIDEO;
      data.store_name = adPlan.store?.label;
      data.page_name = pageOption?.label;
    }
  }

  // Process SB Creatives
  if (adPlan.adType === AD_TYPES.SPONSORED_BRAND.value) {
    if (adPlan.lifestyleImageDetails) {
      data.creative.customImageAssetId = adPlan.lifestyleImageDetails.asset_id;
    }

    if (adPlan.logoDetails) {
      data.creative.brandLogoAssetID = adPlan.logoDetails.asset_id;
    }

    // Product collection and store spotlight formatting
    if (adPlan.newProductCollection || adPlan.storeSpotlight) {
      data.ad_type = AD_TYPES.SPONSORED_BRAND_V2.value;
      data.creative = {
        properties: [
          {
            name: "trellis_brand_ad",
            state: "ENABLED",
            adGroupId: "adGroup1",
            creative: {
              brandLogoAssetID: adPlan.logoDetails?.asset_id,
              headline: adPlan.headline,
              brandName: adPlan.creativeBrandName ? adPlan.creativeBrandName : (brand?.label ?? "Now"),
            },
            landingPage: {
              pageType: "STORE",
              url: pageOption?.value,
              productIds: landingPageProductIds,
            },
          },
        ],
        brand_ad_type:
          adPlan.storeSpotlight ? SB_BRAND_AD_TYPE.STORE_SPOTLIGHT
          : adPlan.newProductCollection ? SB_BRAND_AD_TYPE.PRODUCT_COLLECTION
          : "",
      };

      if (adPlan.newProductCollection) {
        // Amazon turns a Product Collection create into a Manual Collection, which accepts custom
        // images and never renders them, so the server rejects one rather than dropping it
        // silently. An existing Product Collection ad still renders and keeps its image.
        if (adPlan.lifestyleImageDetails && !isCreatingAd) {
          data.creative.properties[0].creative.customImages = [
            { assetId: adPlan.lifestyleImageDetails.asset_id, url: adPlan.lifestyleImageDetails.url_default },
          ];
        }
        data.creative.properties[0].creative.productIds = creativeProductIds;
      }

      if (adPlan.storeSpotlight) {
        // Send the product id the selector recorded and let the server resolve its asin. Plans saved
        // before the id was captured only have the asin, so those pass it through unchanged.
        data.creative.properties[0].creative.subpages = adPlan.storeSpotlightSubpages!.map((subpage) =>
          subpage.productId != null ?
            { url: subpage.url, productId: toInt(subpage.productId), pageTitle: subpage.pageTitle }
          : { url: subpage.url, asin: subpage.asin, pageTitle: subpage.pageTitle },
        );
        const subpageProductIds = adPlan
          .storeSpotlightSubpages!.map((subpage) => (subpage.productId != null ? toInt(subpage.productId) : undefined))
          .filter((id): id is number => id !== undefined);
        // For a plan saved without subpage product ids, the plan's own products *are* the subpage
        // products, so they round-trip unchanged.
        data.products = subpageProductIds.length ? subpageProductIds : productIds;
      }
      data.ad_control_fields.tactic_type =
        adPlan.newProductCollection ? SB_BRAND_AD_TYPE.PRODUCT_COLLECTION
        : adPlan.storeSpotlight ? SB_BRAND_AD_TYPE.STORE_SPOTLIGHT
        : "";
    }

    if (adPlan.manualCollection || adPlan.autoCollection) {
      data.ad_type = AD_TYPES.SPONSORED_BRAND_V2.value;
      const brandAdType =
        adPlan.manualCollection ? SB_BRAND_AD_TYPE.MANUAL_COLLECTION : SB_BRAND_AD_TYPE.AUTO_COLLECTION;

      const collectionCreative: Record<string, unknown> = {
        brandName: adPlan.creativeBrandName ? adPlan.creativeBrandName : (brand?.label ?? "Now"),
      };
      if (adPlan.logoDetails) {
        collectionCreative.brandLogoAssetID = adPlan.logoDetails.asset_id;
      }

      const property: Record<string, unknown> = {
        name: "trellis_brand_ad",
        state: "ENABLED",
        adGroupId: "adGroup1",
        creative: collectionCreative,
      };

      if (adPlan.autoCollection && excludedProductIds?.length) {
        collectionCreative.excludedProductIds = [...new Set(excludedProductIds)];
      }

      if (adPlan.manualCollection) {
        if (adPlan.headline) {
          collectionCreative.headline = adPlan.headline;
        }
        collectionCreative.productIds = creativeProductIds;
        data.products = creativeProductIds;
        // A PRODUCT_LIST page carries no products of its own — Amazon builds it from the
        // creative's asins.
        property.landingPage =
          pageOption?.value ? { pageType: "STORE", url: pageOption.value } : { pageType: "PRODUCT_LIST" };
      }

      data.creative = { properties: [property], brand_ad_type: brandAdType };
      data.ad_control_fields.tactic_type = brandAdType;
    }
  }

  // Sponsored Brand V2 targeting
  if (data.ad_type === AD_TYPES.SPONSORED_BRAND_V2.value) {
    data.ad_control_fields.bid_optimization = adPlan.amazonSBBidOptimization;
    data.ad_control_fields.bid_adjustments_by_shopper_segment = [
      {
        shopperSegment: "NEW_TO_BRAND_PURCHASE",
        percentage: toInt(adPlan.shopperSegmentBidMultiplier) || 0,
      },
    ];
    data.ad_control_fields.bid_adjustments_by_placement = [
      {
        placement: "HOME",
        percentage: toInt(adPlan.homePlacementBidMultiplier) || 0,
      },
      {
        placement: "DETAIL_PAGE",
        percentage: toInt(adPlan.pagePlacementBidMultiplier) || 0,
      },
      {
        placement: "OTHER",
        percentage: toInt(adPlan.otherPlacementBidMultiplier) || 0,
      },
      {
        placement: "TOP_OF_SEARCH",
        percentage: toInt(adPlan.topOfSearchPlacementBidMultiplier) || 0,
      },
    ];

    data.ad_control_fields.campaign_goal = adPlan.sbCampaignGoal ?? SB_CAMPAIGN_GOAL_OPTIONS.PAGE_VISIT.value;
    data.ad_control_fields.sb_cost_control_target_cpc =
      adPlan.costControlTargetCPCEnabled ? adPlan.costControlTargetCPC : null;
  }
  // Sponsored Display Creatives
  if (adPlan.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
    // Only include creative data if at least one creatives field filled out
    if (adPlan.headline || adPlan.lifestyleImageDetails || adPlan.logoDetails || adPlan.videoDetails) {
      data.creative = {
        adGroupId: "adGroup1",
        creativeType: "IMAGE",
        properties: {},
      };
      data.creative_display_metadata = {
        squareCustomImage: {},
        rectCustomImage: {},
      };
    }

    if (adPlan.headline) {
      data.creative.properties.headline = adPlan.headline;
    }

    if (adPlan.lifestyleImageDetails) {
      data.creative.properties.rectCustomImage = {
        assetId: adPlan.lifestyleImageDetails.asset_id,
        assetVersion: adPlan.lifestyleImageDetails.version,
      };
      if (adPlan.sdRectangleImageCroppingCoordinates?.height) {
        data.creative.properties.rectCustomImage.croppingCoordinates = adPlan.sdRectangleImageCroppingCoordinates;
        data.creative_display_metadata.rectCustomImage.croppedArea = adPlan.sdRectangleCroppedArea ?? {};
      }
      data.creative.properties.squareCustomImage = {
        assetId: adPlan.lifestyleImageDetails.asset_id,
        assetVersion: adPlan.lifestyleImageDetails.version,
      };
      if (adPlan.sdSquareImageCroppingCoordinates?.height) {
        data.creative.properties.squareCustomImage.croppingCoordinates = adPlan.sdSquareImageCroppingCoordinates;
        data.creative_display_metadata.squareCustomImage.croppedArea = adPlan.sdSquareCroppedArea ?? {};
      }
    }

    if (adPlan.logoDetails) {
      data.creative.properties.brandLogo = {
        assetId: adPlan.logoDetails.asset_id,
        assetVersion: adPlan.logoDetails.version,
      };
    }

    if (adPlan.videoDetails) {
      data.creative.properties.video = {
        assetId: adPlan.videoDetails.asset_id,
        assetVersion: adPlan.videoDetails.version,
      };
      data.creative.creativeType = "VIDEO";
    }

    if (adPlan.customSdStorePageEnabled && adPlan.page && data.creative) {
      data.landing_page = {
        landingPageType: "STORE",
        landingPageUrl: adPlan.page,
      };
    }

    if (adPlan.filledTargetAudiences?.length) {
      data.custom_audience_id_list = adPlan.filledTargetAudiences.map((audienceTarget) => ({
        id: audienceTarget.value,
        name: audienceTarget.label,
      }));
    }

    data.ad_control_fields.sd_cost_control_target_cpc =
      adPlan.costControlTargetCPCEnabled ? adPlan.costControlTargetCPC : null;
  }

  // Process SP placement multipliers
  if (data.channel === "AMZ" && isAdType([AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value])) {
    const bidAdjustmentsByPlacement: Array<{ placement: string; percentage: number }> = [
      {
        placement: "PLACEMENT_TOP",
        percentage: toInt(adPlan.topOfSearchBidMultiplier) || 0,
      },
      {
        placement: "PLACEMENT_PRODUCT_PAGE",
        percentage: toInt(adPlan.productPageMultiplier) || 0,
      },
      {
        placement: "PLACEMENT_REST_OF_SEARCH",
        percentage: toInt(adPlan.restOfSearchMultiplier) || 0,
      },
    ];
    if (
      AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace) &&
      adPlan.siteRestrictions !== AMAZON_BUSINESS_SITE_RESTRICTION
    ) {
      bidAdjustmentsByPlacement.push({
        placement: "SITE_AMAZON_BUSINESS",
        percentage: toInt(adPlan.amazonBusinessMultiplier) || 0,
      });
    }
    data.ad_control_fields.bid_adjustments_by_placement = bidAdjustmentsByPlacement;
  }
  if (
    adPlan.channel === "AMZ" &&
    isAdType([
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_PRODUCT_AUTO.value,
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_V2.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
    ])
  ) {
    data.ad_control_fields.shopper_cohort_bidding =
      adPlan.audienceBidMultiplierTargetAudienceId ?
        [
          {
            shopperCohortType: "AUDIENCE_SEGMENT",
            percentage: toInt(adPlan.audienceBidMultiplier) || 0,
            audienceSegments: [
              {
                name: adPlan.audienceBidMultiplierTargetAudienceName,
                audienceId: adPlan.audienceBidMultiplierTargetAudienceId.toString(),
                // The type the picker derived from the audience's path. A saved plan the user did
                // not re-pick resends the type it was stored with.
                audienceSegmentType:
                  adPlan.audienceBidMultiplierTargetAudienceSegmentType ??
                  adPlan.audienceBidMultiplierTargetAudienceSegmentTypeFromServer ??
                  AUDIENCE_SEGMENT_TYPES.SPONSORED_ADS_AMC,
              },
            ],
          },
        ]
        // Below is a bit odd, but to remove the audience bid multiplier on Amazon we can't send an empty array and instead need to set the saved % to 0
      : adPlan.savedAudienceBidMultiplierTargetAudienceId && !adPlan.audienceBidMultiplierTargetAudienceId ?
        [
          {
            shopperCohortType: "AUDIENCE_SEGMENT",
            percentage: 0,
            audienceSegments: [
              {
                audienceId: adPlan.savedAudienceBidMultiplierTargetAudienceId.toString(),
                // Zeroed out or not, Amazon still validates the id against the type.
                audienceSegmentType:
                  adPlan.audienceBidMultiplierTargetAudienceSegmentTypeFromServer ??
                  AUDIENCE_SEGMENT_TYPES.SPONSORED_ADS_AMC,
              },
            ],
          },
        ]
      : [];
  }

  if (adPlan.updatedCategoryId && adPlan.id) {
    data.category = toInt(adPlan.updatedCategoryId);
  }

  if (data.channel === "AMZ") {
    for (const campaignType in adPlan.campaignNames) {
      // Remove campaign name suffixes for segmented components used to create unique validation keys
      const trimmed = adPlan.campaignNames[campaignType]?.trim();
      if (adPlan.hasParent) {
        const campaignTypeWithoutSuffix = `${campaignType.split("_")[0]}_name`;
        data.ad_control_fields[campaignTypeWithoutSuffix] = trimmed ? trimmed : null;
      } else {
        data.ad_control_fields[campaignType] = trimmed ? trimmed : null;
      }
    }
  }
  // Handle case where auto added after creation, need auto_name to create campaign mapping in the backend
  if (
    adPlan.id &&
    data.channel === "AMZ" &&
    adPlan.autoCampaignEnabled !== adPlan.autoCampaignEnabledFromServer &&
    adPlan.autoCampaignEnabled
  ) {
    data.ad_control_fields["auto_name"] = null;
  }

  if (
    data.channel === "AMZ" &&
    adPlan.broadMatchEnabled &&
    isAdType([
      AD_TYPES.SPONSORED_PRODUCT.value,
      AD_TYPES.SPONSORED_BRAND.value,
      AD_TYPES.SPONSORED_BRAND_V2.value,
      AD_TYPES.SPONSORED_BRAND_VIDEO.value,
    ])
  ) {
    data.ad_control_fields.broad_match_regular = adPlan.broadMatchRegular;
    data.ad_control_fields.broad_match_modifier = adPlan.broadMatchModifier;
  }

  data.ad_control_fields.program = adPlan.biddingProgram;

  return data;
}
